import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../utils/axios";
import DataTable from "../components/DataTable";
import NewEquipmentModal from "../components/NewEquipmentModal";
import { API_ENDPOINTS } from "../config/api";

const PowerSource = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [nodeDetails, setNodeDetails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [nodeInfo, setNodeInfo] = useState(null);
  const [pageSize, setPageSize] = useState(10);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editNodeData, setEditNodeData] = useState(null);
  const hasFetched = useRef(false);

  const columns = [
    { key: "id", header: "ID" },
    { key: "location", header: "Konum" },
    { key: "kwh", header: "Güç Tüketimi (kWh)" },
    { key: "temp", header: "Sıcaklık (°C)" },
    { key: "humidity", header: "Nem (%)" },
    { key: "pressure", header: "Basınç (hPa)" },
    { key: "startDate", header: "Takip Başlangıç Tarihi" },
    { key: "endDate", header: "Takip Bitiş Tarihi" },
    { key: "duration", header: "Toplam Çalışma Süresi (dk)" },
    { key: "status", header: "Durum" },
  ];

  // Function to convert status number to Turkish text
  const getStatusText = (status) => {
    const statusMap = {
      11: "Arıza",
      22: "Bağlantı Yok",
      31: "Kapalı",
      32: "Pasif",
      33: "Aktif",
    };
    return statusMap[status] || `Status: ${status}`;
  };

  // Function to format location
  const getLocationText = (location) => {
    if (!location || location === null) {
      return "Bilinmeyen";
    }

    // Location is always an object or null
    return formatLocationObject(location);
  };

  // Function to format location object into readable text
  const formatLocationObject = (locationObj) => {
    if (!locationObj) return "Bilinmeyen";

    const parts = [];

    if (locationObj.campus) parts.push(`Kampüs: ${locationObj.campus}`);
    if (locationObj.building) parts.push(`Bina: ${locationObj.building}`);
    if (locationObj.floor) parts.push(`Kat: ${locationObj.floor}`);
    if (locationObj.definition) parts.push(`Tanım: ${locationObj.definition}`);
    if (locationObj.name) parts.push(`Kod: ${locationObj.name}`);

    return parts.length > 0 ? parts.join("  /  ") : "Bilinmeyen";
  };

  // Function to format tracking time
  const getTrackingText = (trackingTime) => {
    if (!trackingTime) {
      return "Takip başlamadı";
    }
    if (typeof trackingTime === "string" && trackingTime.includes("T")) {
      const date = new Date(trackingTime);
      return date.toLocaleString("tr-TR");
    }
    return trackingTime;
  };

  // Function to convert category enum to Turkish
  const getCategoryText = (category) => {
    const categoryMap = {
      Other: "Diğer",
      "Power Supply": "Güç Kaynağı",
      Oscilloscope: "Osiloskop",
      "Network Analyzer": "Ağ Analizörü",
      "Signal Analyzer": "Sinyal Analizörü",
      "Spectrum Analyzer": "Spektrum Analizörü",
      "Power Amplifier": "Güç Yükseltici",
      "IFF Test Device": "IFF Test Cihazı",
    };
    return categoryMap[category] || category || "N/A";
  };

  // Function to format duration from seconds to hours, minutes, seconds
  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;

    if (hours > 0) {
      return `${hours}sa ${minutes}dk ${remainingSeconds}sn`;
    } else if (minutes > 0) {
      return `${minutes}dk ${remainingSeconds}sn`;
    } else {
      return `${remainingSeconds}sn`;
    }
  };

  // Function to fetch node details
  const fetchNodeDetails = async (page = 1, limit = pageSize) => {
    if (!id) return;

    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get(`${API_ENDPOINTS.GET_NODE_DETAILS}/${id}`, {
        params: { page, limit },
      });

      console.log("Node details received:", response.data);

      // Transform actions data to match table structure
      const transformedData =
        response.data.actions?.data?.map((action, index) => {
          // Format location object
          const locationText = getLocationText(action.location);

          return {
            id: index + 1, // Sıralı numara (1, 2, 3, ...)
            location: locationText,
            status: getStatusText(action.status),
            startDate: getTrackingText(action.startDate),
            endDate: getTrackingText(action.endDate),
            duration: action.timeDiff ? formatDuration(action.timeDiff) : "N/A",
            kwh: action.kwh !== undefined && action.kwh !== null ? action.kwh.toFixed(6) : "N/A",
            temp: action.temp !== undefined && action.temp !== null ? `${action.temp.toFixed(2)}` : "N/A",
            humidity: action.humidity !== undefined && action.humidity !== null ? `${action.humidity.toFixed(2)}` : "N/A",
            pressure: action.pressure !== undefined && action.pressure !== null ? `${action.pressure.toFixed(2)}` : "N/A",
          };
        }) || [];

      setNodeDetails(transformedData);

      // Set pagination info from actions.pagination
      if (response.data.actions?.pagination) {
        setTotalPages(response.data.actions.pagination.totalPages || 1);
      }

      // Set node info for header from metadata
      if (response.data.metadata) {
        setNodeInfo({
          ...response.data.metadata,
          deviceId: response.data.node?.deviceId, // Add deviceId to nodeInfo
        });
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || "Veri yüklenirken hata oluştu";
      setError(errorMessage);
      console.error("Error fetching node details:", err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch data when component mounts or page/size changes
  useEffect(() => {
    fetchNodeDetails(currentPage, pageSize);
  }, [id, currentPage, pageSize]);

  // Handle page change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // Handle page size change
  const handlePageSizeChange = (newSize) => {
    setPageSize(parseInt(newSize));
    setCurrentPage(1); // Reset to first page when changing size
  };

  // Handle edit tracker button click
  const handleEditTracker = async () => {
    if (!id) {
      setError("Node ID bulunamadı");
      return;
    }

    try {
      setLoading(true);
      const response = await axiosInstance.get(`${API_ENDPOINTS.GET_NODE_DETAILS_BY_ID}/${id}`);
      console.log("Node details for editing:", response.data);

      // Transform the data for the modal
      const nodeData = {
        deviceId: response.data.deviceId || nodeInfo?.deviceId || "",
        brand: response.data.metadata?.brand || "",
        model: response.data.metadata?.model || "",
        investmentInventoryCategory: response.data.metadata?.investmentInventoryCategory || "",
        fixedAssetNumber: response.data.metadata?.fixedAssetNumber || "",
        tagNumber: response.data.metadata?.tagNumber || "",
        yearOfInventory: response.data.metadata?.yearOfInventory || "",
      };

      setEditNodeData(nodeData);
      setIsEditModalOpen(true);
    } catch (err) {
      console.error("Error fetching node details for editing:", err);
      setError("Node detayları yüklenirken hata oluştu");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <button onClick={() => navigate("/dashboard")} className="text-gray-600 hover:text-gray-800 flex items-center space-x-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span>Geri</span>
          </button>
          <h1 className="text-2xl font-bold text-gray-800">
            {nodeInfo ? (
              <>
                <span className="font-bold">
                  {nodeInfo.brand || ""} {nodeInfo.model || ""}
                </span>
                <span className="text-lg font-normal text-gray-600 ml-2">{getCategoryText(nodeInfo.investmentInventoryCategory)}</span>
                <span className="text-lg font-normal text-gray-600 ml-2">{nodeInfo.yearOfInventory || ""}</span>
              </>
            ) : (
              `Node ${id}`
            )}
          </h1>
        </div>
        <div className="flex items-center space-x-4">
          {/* Page Size Selector */}
          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-600">Sayfa başına:</label>
            <select
              value={pageSize}
              onChange={(e) => handlePageSizeChange(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>

          <button
            onClick={handleEditTracker}
            className="text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors hover:opacity-90"
            style={{ backgroundColor: "#1570EF" }}
          >
            <span>Tracker Düzenle</span>
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">{error}</div>}

      {/* Data Table */}
      {loading ? (
        <div className="flex justify-center items-center py-8">
          <div className="text-gray-600">Veriler yükleniyor...</div>
        </div>
      ) : nodeDetails.length === 0 ? (
        <div className="flex justify-center items-center py-12">
          <div className="text-center">
            <div className="text-gray-500 text-lg mb-2">📋</div>
            <div className="text-gray-600 text-lg">Veri bulunamadı</div>
            <div className="text-gray-400 text-sm mt-1">Bu node için detay bulunamadı</div>
          </div>
        </div>
      ) : (
        <DataTable data={nodeDetails} columns={columns} currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} pageSize={pageSize} />
      )}

      {/* Edit Modal */}
      <NewEquipmentModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditNodeData(null);
          // Refresh data after modal closes
          fetchNodeDetails(currentPage, pageSize);
        }}
        initialData={editNodeData}
        isEditMode={true}
      />
    </div>
  );
};

export default PowerSource;
